/**
 * Track Participant Groups Store
 *
 * Track-level group participation (track_participant_groups).
 * Effective track participants = cycle (users + groups) + track users + this table.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { ParticipantRole } from './cycleParticipantsStore';

// ============================================================================
// Types
// ============================================================================

export type TrackParticipantGroupStatus = 'active' | 'removed';

export interface TrackParticipantGroup {
  id: string;
  trackId: string;
  groupId: string;
  role: ParticipantRole;
  status: TrackParticipantGroupStatus;
  addedBy: string;
  addedAt: Date;
  removedAt?: Date;
  removedBy?: string;
}

interface TrackParticipantGroupsState {
  participantGroups: TrackParticipantGroup[];
  isLoading: boolean;
  error: string | null;

  fetchParticipantGroupsForTrack: (trackId: string) => Promise<void>;
  addParticipantGroup: (input: {
    trackId: string;
    groupId: string;
    role: ParticipantRole;
    addedBy: string;
  }) => Promise<TrackParticipantGroup | null>;
  removeParticipantGroup: (id: string, removedBy: string) => Promise<boolean>;

  getParticipantGroupsForTrack: (trackId: string) => TrackParticipantGroup[];
  getActiveParticipantGroupsForTrack: (trackId: string) => TrackParticipantGroup[];
}

const TABLE_NAME = 'track_participant_groups';

function rowToParticipantGroup(row: Record<string, unknown>): TrackParticipantGroup {
  return {
    id: row.id as string,
    trackId: row.track_id as string,
    groupId: row.group_id as string,
    role: row.role as ParticipantRole,
    status: (row.status as TrackParticipantGroupStatus) || 'active',
    addedBy: row.added_by as string,
    addedAt: row.added_at != null ? new Date(row.added_at as string) : new Date(0),
    removedAt: row.removed_at ? new Date(row.removed_at as string) : undefined,
    removedBy: row.removed_by as string | undefined,
  };
}

function participantGroupToRow(p: Partial<TrackParticipantGroup>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.id != null) out.id = p.id;
  if (p.trackId != null) out.track_id = p.trackId;
  if (p.groupId != null) out.group_id = p.groupId;
  if (p.role != null) out.role = p.role;
  if (p.status != null) out.status = p.status;
  if (p.addedBy != null) out.added_by = p.addedBy;
  if (p.addedAt != null) out.added_at = p.addedAt.toISOString();
  if (p.removedAt !== undefined) out.removed_at = p.removedAt?.toISOString() ?? null;
  if (p.removedBy !== undefined) out.removed_by = p.removedBy ?? null;
  return out;
}

// ============================================================================
// Store
// ============================================================================

export const useTrackParticipantGroupsStore = create<TrackParticipantGroupsState>()((set, get) => ({
  participantGroups: [],
  isLoading: false,
  error: null,

  fetchParticipantGroupsForTrack: async (trackId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('track_id', trackId)
        .order('added_at', { ascending: true });
      if (error) {
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          set(state => {
            const other = state.participantGroups.filter(g => g.trackId !== trackId);
            return { participantGroups: other, isLoading: false, error: null };
          });
          return;
        }
        set({ isLoading: false, error: error.message });
        return;
      }
      const list = (data || []).map(rowToParticipantGroup);
      set(state => {
        const other = state.participantGroups.filter(g => g.trackId !== trackId);
        return { participantGroups: [...other, ...list], isLoading: false, error: null };
      });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipantGroup: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const { data: existing } = await supabase
        .from(TABLE_NAME)
        .select('id, status')
        .eq('track_id', input.trackId)
        .eq('group_id', input.groupId)
        .maybeSingle();
      if (existing) {
        if (existing.status === 'removed') {
          const { data: updated, error: upErr } = await supabase
            .from(TABLE_NAME)
            .update({ status: 'active', removed_at: null, removed_by: null })
            .eq('id', existing.id)
            .select()
            .single();
          if (upErr) {
            set({ error: upErr.message });
            return null;
          }
          const g = rowToParticipantGroup(updated);
          set(state => ({
            participantGroups: state.participantGroups.map(x => (x.id === g.id ? g : x)),
            error: null,
          }));
          return g;
        }
        return rowToParticipantGroup(existing as unknown as Record<string, unknown>);
      }
      const newRow: TrackParticipantGroup = {
        id: generateUUID(),
        trackId: input.trackId,
        groupId: input.groupId,
        role: input.role,
        status: 'active',
        addedBy: input.addedBy,
        addedAt: new Date(),
      };
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(participantGroupToRow(newRow))
        .select()
        .single();
      if (error) {
        set({ error: error.message });
        return null;
      }
      const inserted = rowToParticipantGroup(data as unknown as Record<string, unknown>);
      set(state => ({ participantGroups: [...state.participantGroups, inserted], error: null }));
      return inserted;
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  removeParticipantGroup: async (id, removedBy) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const g = get().participantGroups.find(x => x.id === id);
    if (!g) return false;
    const now = new Date();
    try {
      // Ask for the row back: a write the row rules refuse changes nothing and
      // still reports no error, so an empty answer is the refusal.
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .update({ status: 'removed', removed_at: now.toISOString(), removed_by: removedBy })
        .eq('id', id)
        .select('id');
      if (error || !data?.length) {
        set({ error: error?.message ?? 'The server did not save the removal.' });
        return false;
      }
      set(state => ({
        participantGroups: state.participantGroups.map(x =>
          x.id === id ? { ...x, status: 'removed' as TrackParticipantGroupStatus, removedAt: now, removedBy } : x
        ),
        error: null,
      }));
      return true;
    } catch (err) {
      set({ error: String(err) });
      return false;
    }
  },

  getParticipantGroupsForTrack: (trackId) =>
    get().participantGroups.filter(g => g.trackId === trackId),

  getActiveParticipantGroupsForTrack: (trackId) =>
    get().participantGroups.filter(g => g.trackId === trackId && g.status === 'active'),
}));
